import { Injectable } from '@angular/core';
import { ColorTarget, EncField, MarkType } from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { DialogService } from '@mode/shared/ui';
import { ColorsModalComponent } from './colors-modal.component';
import * as ColorModalHelpers from './colors-modal-helpers';
import { CompiledAttr, CompiledField, Switch } from '@mode/shared/util-switch';
import { FieldListDomainLoader } from './field-list-domain-loader';
import { EmptyDomainLoader } from './empty-domain-loader';
import { QueryDomainLoader } from './query-domain-loader';
import { RenderDataModelDomainLoader } from './render-data-model-domain-loader';
import { ErrorDomainLoader } from './error-domain-loader';
import { ColorsModalFacade, ColorsModalData } from '@mode/charts/contract';

@Injectable()
export class ColorsModalService implements ColorsModalFacade {
  constructor(private dialogService: DialogService) {}

  public open(data: ColorsModalData): void {
    if (data.encoding == null) {
      throw Error('Encoding is required to open the color modal');
    }
    const currentField = CompiledField.fromEncField(data.view, data.colorField);
    const isFlatTableEncoding = ColorModalHelpers.isFlatTableEncoding(currentField, data.encoding);
    const allColorFieldsInEncoding = isFlatTableEncoding
      ? [currentField]
      : ColorModalHelpers.getColorCompiledFields(data.encoding, data.view, data.chartHasSplitLegend);

    const summary = EncodingHelpers.getSummary(data.encoding);
    const isText = summary?.type === MarkType.Text && !isFlatTableEncoding;
    const canUseAsBackground = isText && data.encoding.marks.channel === '*';
    const isUseAsBackground = canUseAsBackground && data.encoding.marks.properties.color.target === 'background';

    const domainLoader = (() => {
      if (data.renderModel == null && EncodingHelpers.isEncodingEmpty(data.encoding)) {
        return new EmptyDomainLoader();
      } else if (data.isManualUpdateMode || data.renderModel == null) {
        return new QueryDomainLoader(data.view, data.encoding, data.queryRunToken, data.vizToken, isFlatTableEncoding);
      } else if (data.renderModel) {
        return new RenderDataModelDomainLoader(data.encoding, data.renderModel, data.view);
      }
      return new ErrorDomainLoader();
    })();

    this.dialogService.open({
      component: ColorsModalComponent,
      matDialogConfig: {
        backdropClass: 'mode-filter-modal-backdrop',
        data: {
          ...data,
          currentField,
          allFields: allColorFieldsInEncoding,
          isText,
          canUseAsBackground,
          isUseAsBackground,
          isFieldLevel: false,
          domainLoader,
        },
      },
    });
  }

  public openFromFieldList(
    primaryField: FlamingoTypes.ModeAttr,
    view: FlamingoTypes.ModeView,
    palettes: readonly OrgTypes.ColorPalette[],
    queryRunToken: string,
    applyColors: (colorField: EncField, fieldColor: ChartTypes.FieldColor, colorTarget: ColorTarget) => void,
    onCancel: () => void
  ): void {
    const allColorFieldsInEncoding = view.attrs
      .map((modeAttr, index) => {
        const field = CompiledAttr.fromAttr(view, modeAttr);
        return CompiledField.fromEncField(view, { ...field.toEncField(), id: index.toString(16).padStart(4) });
      })
      .filter((field) => field.isAggregateable || field.isFormulaNumeric);
    const currentField = allColorFieldsInEncoding.find((field) => field.attrName === primaryField.name);

    // Check for dimension
    const model = Switch.Models.make('model', [view]);
    const dimensions = Switch.Attrs.dimensions(model, view.name);
    const isDimension = dimensions.find((attr) => attr.name === primaryField.name);
    const expectedPaletteType = isDimension
      ? OrgTypes.ColorPaletteType.Categorical
      : OrgTypes.ColorPaletteType.Sequential;
    const defaultPalette = palettes.find((palette) => palette.isDefault && palette.type === expectedPaletteType);

    const domainLoader = new FieldListDomainLoader(view, queryRunToken);

    this.dialogService.open({
      component: ColorsModalComponent,
      matDialogConfig: {
        backdropClass: 'mode-filter-modal-backdrop',
        data: {
          palettes,
          currentPaletteToken: defaultPalette?.token ?? '',
          queryRunToken,
          vizToken: '',
          view,
          encoding: undefined,
          colorField: undefined,
          renderModel: undefined,
          fieldColors: {},
          fieldFormats: {},
          chartHasSplitLegend: false,
          isManualUpdateMode: false,
          savedColorTarget: 'background',
          isChildModal: false,
          hideRangeControls: true,
          hideSteps: false,
          hideStepLabels: true,
          currentField,
          allFields: allColorFieldsInEncoding,
          canUseAsBackground: false,
          isUseAsBackground: false,
          isText: false,
          isFieldLevel: true,
          domainLoader,
          applyColors,
          onCancel,
        },
      },
    });
  }
}
